import React from 'react';
import { Database, Upload, FileText, PenLine, Calendar } from 'lucide-react';

/**
 * DataSourceBadge - Shows the source of data with a small badge
 *
 * @param {string} source - Source identifier (PMS, RMS, OTB, Manual, Budget, etc.)
 * @param {string} className - Additional CSS classes
 * @param {boolean} showTooltip - Whether to show a tooltip on hover
 * @param {string} importDate - Optional import date to show in tooltip
 * @param {number} completeness - Optional completeness percentage (0-100)
 */
const DataSourceBadge = ({
  source,
  className = '',
  showTooltip = true,
  importDate = null,
  completeness = null
}) => {
  if (!source) return null;

  // Map source to configuration (icon, color, label)
  const sourceConfig = {
    PMS: {
      icon: Database,
      color: 'blue',
      label: 'PMS',
      description: 'Property Management System'
    },
    RMS: {
      icon: Calendar,
      color: 'purple',
      label: 'RMS',
      description: 'Revenue Management System'
    },
    OTB: {
      icon: FileText,
      color: 'green',
      label: 'OTB',
      description: 'On-The-Books Forecast'
    },
    Manual: {
      icon: PenLine,
      color: 'orange',
      label: 'Manual',
      description: 'Manually entered'
    },
    Budget: {
      icon: FileText,
      color: 'gray',
      label: 'Budget',
      description: 'Budget data'
    },
    Import: {
      icon: Upload,
      color: 'indigo',
      label: 'Import',
      description: 'Imported data'
    }
  };

  const config = sourceConfig[source] || {
    icon: Database,
    color: 'gray',
    label: source,
    description: 'Unknown source'
  };

  const Icon = config.icon;

  // Color classes
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };

  const hoverColorClasses = {
    blue: 'hover:bg-blue-100',
    purple: 'hover:bg-purple-100',
    green: 'hover:bg-green-100',
    orange: 'hover:bg-orange-100',
    gray: 'hover:bg-gray-100',
    indigo: 'hover:bg-indigo-100'
  };

  // Completeness color (for progress indicator)
  const getCompletenessColor = (value) => {
    if (value >= 80) return 'bg-green-500';
    if (value >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const tooltipContent = showTooltip ? (
    <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block z-50">
      <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg whitespace-nowrap">
        <div className="font-semibold mb-0.5">{config.description}</div>
        {importDate && (
          <div className="text-gray-300">
            Imported: {new Date(importDate).toLocaleDateString()}
          </div>
        )}
        {completeness !== null && (
          <div className="text-gray-300 mt-1">
            Completeness: {completeness.toFixed(0)}%
          </div>
        )}
        {/* Arrow pointing down */}
        <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
      </div>
    </div>
  ) : null;

  return (
    <div className={`group relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium transition-colors ${colorClasses[config.color]} ${hoverColorClasses[config.color]} ${className}`}>
      <Icon size={10} />
      <span>{config.label}</span>
      {completeness !== null && (
        <div className="ml-1 w-8 bg-gray-200 rounded-full h-1">
          <div
            className={`h-1 rounded-full ${getCompletenessColor(completeness)}`}
            style={{ width: `${completeness}%` }}
          />
        </div>
      )}
      {tooltipContent}
    </div>
  );
};

export default DataSourceBadge;
